import React from 'react';
import { motion } from 'framer-motion';
import { Languages, Mic, Camera, Volume2, History, ArrowRightLeft } from 'lucide-react';

const TranslatorFeatures = () => {
  const features = [
    {
      icon: Languages,
      title: "Smart Translation",
      description: "Powered by Google ML Kit, translate between 8 major languages with 95%+ accuracy.",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: Mic,
      title: "Voice Input",
      description: "Speak naturally and get instant text-to-translation conversion with advanced speech recognition.",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      icon: Camera,
      title: "Camera OCR",
      description: "Point your camera at text or select photos to extract and translate text instantly.",
      gradient: "from-green-500 to-emerald-500"
    },
    {
      icon: Volume2,
      title: "Text-to-Speech",
      description: "Hear perfect pronunciation in your target language with natural voice synthesis.",
      gradient: "from-orange-500 to-red-500"
    },
    {
      icon: History,
      title: "Smart History",
      description: "Automatically save translations with powerful search and favorites management.",
      gradient: "from-indigo-500 to-purple-500"
    },
    {
      icon: ArrowRightLeft,
      title: "Language Swap",
      description: "One-tap language switching for quick bidirectional translation conversations.",
      gradient: "from-teal-500 to-blue-500"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Powerful Features for
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              Seamless Communication
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience cutting-edge AI technology that makes language barriers disappear.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              {/* Icon */}
              <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-r ${feature.gradient} text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className="w-7 h-7" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Tech Stack */}
        <motion.div 
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-8">
            Powered by Industry-Leading Technology
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            <span className="text-lg font-medium text-gray-900">Google ML Kit</span>
            <span className="text-lg font-medium text-gray-900">Material Design 3</span>
            <span className="text-lg font-medium text-gray-900">Advanced OCR</span>
            <span className="text-lg font-medium text-gray-900">Neural TTS</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TranslatorFeatures; 